/**
 * Contact detail/edit form — every §2 group (contacts handoff §2.1):
 * identity, affiliation, multi-value editors (phones/emails/channels/
 * social/addresses with label+primary), OPEN relationship combobox,
 * trust-tier selector with plain-language meanings + explicit confirm
 * (CT-INV-7/8), disposition, handlingNotes, links, notes, archive
 * (soft-delete only).
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGatewayStore } from "@/stores/useGatewayStore";
import {
  addEntry,
  emptyContactDraft,
  removeEntry,
  setPrimary,
  TIER_META,
  tierChangeNeedsConfirm,
} from "@/lib/contacts-logic";
import type { Contact, ContactDraft, ContactEmail, ContactPhone, ContactSocial, Disposition, TrustTier } from "@/types/contacts";
import { DISPOSITIONS, OWNER_CONTACT_ID, RELATIONSHIP_SEEDS, TRUST_TIERS } from "@/types/contacts";

interface Props {
  contactId?: string;
  existing?: Contact;
  backendPending: boolean;
  onSaved: (saved: Contact) => void;
  onArchivedChange: () => void;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-5">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h3>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function TextField({
  label,
  value,
  onChange,
  testid,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  testid?: string;
}) {
  return (
    <label className="block text-sm">
      <span className="mb-0.5 block text-xs text-slate-500">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded border border-slate-300 bg-transparent px-2 py-1 dark:border-slate-700"
        data-testid={testid}
      />
    </label>
  );
}

/** Generic multi-value editor row list (add / remove / label / primary). */
function MultiValueEditor<T extends { label?: string; primary?: boolean }>({
  entries,
  onChange,
  mainField,
  mainPlaceholder,
  getMain,
  setMain,
  makeEmpty,
  addLabel,
  testid,
}: {
  entries: T[];
  onChange: (next: T[]) => void;
  mainField: string;
  mainPlaceholder: string;
  getMain: (e: T) => string;
  setMain: (e: T, v: string) => T;
  makeEmpty: () => T;
  addLabel: string;
  testid: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1.5" data-testid={testid}>
      {entries.map((entry, i) => (
        <div key={`${mainField}-${i}`} className="flex items-center gap-1.5 text-sm">
          <input
            type="text"
            value={getMain(entry)}
            placeholder={mainPlaceholder}
            onChange={(e) => {
              const next = [...entries];
              next[i] = setMain(entry, e.target.value);
              onChange(next);
            }}
            className="min-w-0 flex-1 rounded border border-slate-300 bg-transparent px-2 py-1 dark:border-slate-700"
            data-testid={`${testid}_main_${i}`}
          />
          <input
            type="text"
            value={entry.label ?? ""}
            placeholder={t("contacts.entryLabel", "rótulo")}
            onChange={(e) => {
              const next = [...entries];
              next[i] = { ...entry, label: e.target.value || undefined };
              onChange(next);
            }}
            className="w-28 rounded border border-slate-300 bg-transparent px-2 py-1 text-xs dark:border-slate-700"
          />
          <button
            type="button"
            title={t("contacts.setPrimary", "Tornar principal")}
            onClick={() => onChange(setPrimary(entries, i))}
            className={`rounded px-1.5 py-1 text-xs ${
              entry.primary
                ? "bg-amber-500/20 text-amber-600"
                : "text-slate-400 hover:text-amber-600"
            }`}
            data-testid={`${testid}_primary_${i}`}
          >
            ★
          </button>
          <button
            type="button"
            title={t("common.remove", "Remover")}
            onClick={() => onChange(removeEntry(entries, i))}
            className="rounded px-1.5 py-1 text-xs text-slate-400 hover:text-red-500"
            data-testid={`${testid}_remove_${i}`}
          >
            ✕
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange(addEntry(entries, makeEmpty()))}
        className="text-xs text-amber-600 hover:underline"
        data-testid={`${testid}_add`}
      >
        + {addLabel}
      </button>
    </div>
  );
}

export function ContactDetailForm({ contactId, existing, backendPending, onSaved, onArchivedChange }: Props) {
  const { t } = useTranslation();
  const rpc = useGatewayStore((s) => s.rpc);

  const initial: ContactDraft = useMemo(
    () => (existing ? { ...existing } : emptyContactDraft()),
    [existing],
  );
  const [draft, setDraft] = useState<ContactDraft>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Pending tier change awaiting the explicit restate-the-meaning confirm. */
  const [tierConfirm, setTierConfirm] = useState<TrustTier | null>(null);

  const isOwner = contactId === OWNER_CONTACT_ID;
  const isNew = !contactId && !existing;

  function patch(p: Partial<ContactDraft>) {
    setDraft((d) => ({ ...d, ...p }));
  }

  function requestTierChange(next: TrustTier) {
    const from = (draft.trustTier ?? "known") as TrustTier;
    if (!tierChangeNeedsConfirm(from, next)) {
      patch({ trustTier: next });
      return;
    }
    setTierConfirm(next);
  }

  async function save() {
    if (!rpc) return;
    setSaving(true);
    setError(null);
    try {
      const res = await rpc.contactsUpsert({
        contact: { ...draft, id: contactId ?? draft.id },
      });
      onSaved(res.contact);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  async function toggleArchive() {
    if (!rpc || !contactId) return;
    const archiving = draft.status !== "archived";
    const msg = archiving
      ? t(
          "contacts.archiveConfirm",
          "Arquivar este contato? O registro é preservado (nada é apagado); as identidades de canal dele voltam a ser tratadas como desconhecidas.",
        )
      : t("contacts.unarchiveConfirm", "Restaurar este contato do arquivo?");
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return;
    try {
      await rpc.contactsArchive({ contactId, archived: archiving });
      patch({ status: archiving ? "archived" : "active" });
      onArchivedChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div className="mx-auto max-w-2xl" data-testid="contact_detail_form">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">
          {isNew
            ? t("contacts.newTitle", "Novo contato")
            : draft.displayName || t("contacts.untitled", "(sem nome)")}
          {isOwner && (
            <span className="ml-2 rounded bg-amber-500/15 px-2 py-0.5 text-xs text-amber-600">
              {t("contacts.ownerBadge", "seu próprio registro")}
            </span>
          )}
        </h2>
        <div className="flex gap-2">
          {!isNew && !isOwner && (
            <button
              type="button"
              onClick={toggleArchive}
              className="rounded border border-slate-300 px-3 py-1 text-sm text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-900"
              data-testid="contact_archive"
            >
              {draft.status === "archived"
                ? t("contacts.unarchive", "Restaurar")
                : t("contacts.archive", "Arquivar")}
            </button>
          )}
          <button
            type="button"
            disabled={saving || backendPending || !draft.displayName?.trim()}
            onClick={() => void save()}
            className="rounded bg-amber-600 px-4 py-1 text-sm font-medium text-white hover:bg-amber-500 disabled:opacity-50"
            data-testid="contact_save"
          >
            {saving ? t("common.saving", "Salvando…") : t("common.save", "Salvar")}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 rounded border border-red-400/50 bg-red-50 p-2 text-xs text-red-800 dark:bg-red-950/40 dark:text-red-200">
          {error}
        </div>
      )}

      <Section title={t("contacts.sectionIdentity", "Identidade")}>
        <div className="grid grid-cols-2 gap-2">
          <label className="block text-sm">
            <span className="mb-0.5 block text-xs text-slate-500">{t("contacts.kind", "Tipo")}</span>
            <select
              value={draft.kind}
              onChange={(e) => patch({ kind: e.target.value as "person" | "organization" })}
              className="w-full rounded border border-slate-300 bg-transparent px-2 py-1 dark:border-slate-700"
            >
              <option value="person">{t("contacts.kindPerson", "Pessoa")}</option>
              <option value="organization">{t("contacts.kindOrg", "Organização")}</option>
            </select>
          </label>
          <TextField
            label={t("contacts.displayName", "Nome de exibição *")}
            value={draft.displayName}
            onChange={(v) => patch({ displayName: v })}
            testid="contact_display_name"
          />
          <TextField
            label={t("contacts.fullLegalName", "Nome completo/razão social")}
            value={draft.fullLegalName ?? ""}
            onChange={(v) => patch({ fullLegalName: v || undefined })}
          />
          <TextField
            label={draft.kind === "organization" ? "CNPJ" : "CPF"}
            value={(draft.kind === "organization" ? draft.cnpj : draft.cpf) ?? ""}
            onChange={(v) =>
              draft.kind === "organization"
                ? patch({ cnpj: v || undefined })
                : patch({ cpf: v || undefined })
            }
          />
          <TextField
            label={t("contacts.oab", "OAB")}
            value={draft.oabNumber ?? ""}
            onChange={(v) => patch({ oabNumber: v || undefined })}
          />
          <TextField
            label={t("contacts.treatment", "Tratamento (Dra., Sr., …)")}
            value={draft.treatment ?? ""}
            onChange={(v) => patch({ treatment: v || undefined })}
          />
        </div>
      </Section>

      <Section title={t("contacts.sectionAffiliation", "Vínculo profissional")}>
        <div className="grid grid-cols-3 gap-2">
          <TextField
            label={t("contacts.orgName", "Organização")}
            value={draft.organizationName ?? ""}
            onChange={(v) => patch({ organizationName: v || undefined })}
          />
          <TextField
            label={t("contacts.jobTitle", "Cargo")}
            value={draft.jobTitle ?? ""}
            onChange={(v) => patch({ jobTitle: v || undefined })}
          />
          <TextField
            label={t("contacts.department", "Departamento")}
            value={draft.department ?? ""}
            onChange={(v) => patch({ department: v || undefined })}
          />
        </div>
      </Section>

      <Section title={t("contacts.sectionPhones", "Telefones")}>
        <MultiValueEditor<ContactPhone>
          entries={draft.phones}
          onChange={(phones) => patch({ phones })}
          mainField="number"
          mainPlaceholder="+55 11 9…"
          getMain={(e) => e.number}
          setMain={(e, v) => ({ ...e, number: v })}
          makeEmpty={() => ({ number: "" })}
          addLabel={t("contacts.addPhone", "telefone")}
          testid="contact_phones"
        />
      </Section>

      <Section title={t("contacts.sectionEmails", "E-mails")}>
        <MultiValueEditor<ContactEmail>
          entries={draft.emails}
          onChange={(emails) => patch({ emails })}
          mainField="address"
          mainPlaceholder="nome@exemplo.com"
          getMain={(e) => e.address}
          setMain={(e, v) => ({ ...e, address: v })}
          makeEmpty={() => ({ address: "" })}
          addLabel={t("contacts.addEmail", "e-mail")}
          testid="contact_emails"
        />
      </Section>

      <Section title={t("contacts.sectionChannels", "Identidades de canal (Telegram/WhatsApp)")}>
        {draft.channels.length === 0 ? (
          <p className="text-xs text-slate-500">
            {t(
              "contacts.channelsHint",
              "Vínculos de canal são criados pelos fluxos de pareamento/verificação — nunca à mão. Os existentes aparecem aqui.",
            )}
          </p>
        ) : (
          draft.channels.map((ch, i) => (
            <div key={`${ch.channel}:${ch.peerId}`} className="flex items-center gap-2 text-sm">
              <span className="rounded bg-slate-500/10 px-1.5 py-0.5 text-xs">{ch.channel}</span>
              <code className="text-xs">{ch.peerId}</code>
              {ch.verified ? (
                <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-600">
                  {t("contacts.verified", "verificado")}
                </span>
              ) : (
                <span className="rounded bg-slate-500/15 px-1.5 py-0.5 text-[10px] text-slate-500">
                  {t("contacts.unverified", "não verificado")}
                </span>
              )}
              <button
                type="button"
                className="ml-auto text-xs text-slate-400 hover:text-red-500"
                data-testid={`contact_unbind_${i}`}
                onClick={() => {
                  // eslint-disable-next-line no-alert
                  if (
                    contactId &&
                    window.confirm(
                      t(
                        "contacts.unbindConfirm",
                        "Desvincular esta identidade de canal? Mensagens futuras desse remetente voltarão a ser tratadas como de desconhecido.",
                      ),
                    )
                  ) {
                    void rpc
                      ?.contactsUnbindPeer({ contactId, channel: ch.channel, peerId: ch.peerId })
                      .then(() =>
                        patch({ channels: draft.channels.filter((_, j) => j !== i) }),
                      )
                      .catch((err: unknown) =>
                        setError(err instanceof Error ? err.message : String(err)),
                      );
                  }
                }}
              >
                {t("contacts.unbind", "desvincular")}
              </button>
            </div>
          ))
        )}
      </Section>

      <Section title={t("contacts.sectionSocial", "Redes / web")}>
        <MultiValueEditor<ContactSocial & { primary?: boolean }>
          entries={draft.social}
          onChange={(social) =>
            patch({ social: social.map(({ primary: _p, ...rest }) => rest) })
          }
          mainField="handleOrUrl"
          mainPlaceholder="@perfil ou URL"
          getMain={(e) => e.handleOrUrl}
          setMain={(e, v) => ({ ...e, handleOrUrl: v })}
          makeEmpty={() => ({ network: "site", handleOrUrl: "" })}
          addLabel={t("contacts.addSocial", "rede/site")}
          testid="contact_social"
        />
      </Section>

      <Section title={t("contacts.sectionClassification", "Classificação")}>
        <div className="grid grid-cols-2 gap-2">
          <label className="block text-sm">
            <span className="mb-0.5 block text-xs text-slate-500">
              {t("contacts.relationship", "Relação (livre)")}
            </span>
            <input
              type="text"
              list="contact-relationship-seeds"
              value={draft.relationship}
              onChange={(e) => patch({ relationship: e.target.value })}
              className="w-full rounded border border-slate-300 bg-transparent px-2 py-1 dark:border-slate-700"
              data-testid="contact_relationship"
            />
            <datalist id="contact-relationship-seeds">
              {RELATIONSHIP_SEEDS.map((r) => (
                <option key={r} value={r} />
              ))}
            </datalist>
          </label>
          <TextField
            label={t("contacts.tags", "Tags (separadas por vírgula)")}
            value={draft.tags.join(", ")}
            onChange={(v) =>
              patch({ tags: v.split(",").map((s) => s.trim()).filter(Boolean) })
            }
          />
        </div>
      </Section>

      <Section title={t("contacts.sectionTrust", "Confiança & tratamento de mensagens")}>
        <div className="space-y-1" data-testid="contact_tier_selector">
          {TRUST_TIERS.map((tt) => {
            const meta = TIER_META[tt];
            const active = draft.trustTier === tt;
            const ownerLocked = isOwner && tt !== "owner";
            return (
              <label
                key={tt}
                className={`flex cursor-pointer items-start gap-2 rounded border p-2 text-sm ${
                  active
                    ? "border-amber-500 bg-amber-500/5"
                    : "border-slate-200 dark:border-slate-800"
                } ${ownerLocked ? "opacity-40" : ""}`}
              >
                <input
                  type="radio"
                  name="trustTier"
                  checked={active}
                  disabled={ownerLocked}
                  onChange={() => requestTierChange(tt)}
                  data-testid={`contact_tier_${tt}`}
                />
                <span>
                  <span className="font-medium">{meta.label}</span>
                  <span className="block text-xs text-slate-500">{meta.description}</span>
                </span>
              </label>
            );
          })}
        </div>
        <label className="block text-sm">
          <span className="mb-0.5 block text-xs text-slate-500">
            {t("contacts.disposition", "Disposição padrão (o que fazer quando escrever)")}
          </span>
          <select
            value={draft.disposition}
            onChange={(e) => patch({ disposition: e.target.value as Disposition })}
            className="w-full rounded border border-slate-300 bg-transparent px-2 py-1 dark:border-slate-700"
            data-testid="contact_disposition"
          >
            {DISPOSITIONS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          <span className="mb-0.5 block text-xs text-slate-500">
            {t(
              "contacts.handlingNotes",
              "Instruções de tratamento (o assistente segue ao falar com este contato)",
            )}
          </span>
          <textarea
            value={draft.handlingNotes ?? ""}
            onChange={(e) => patch({ handlingNotes: e.target.value || undefined })}
            rows={3}
            className="w-full rounded border border-slate-300 bg-transparent px-2 py-1 dark:border-slate-700"
            data-testid="contact_handling_notes"
          />
        </label>
      </Section>

      <Section title={t("contacts.sectionLinks", "Vínculos")}>
        <div className="grid grid-cols-2 gap-2">
          <TextField
            label={t("contacts.clientId", "Cliente (clientId)")}
            value={draft.clientId ?? ""}
            onChange={(v) => patch({ clientId: v || undefined })}
          />
          <TextField
            label={t("contacts.userId", "Usuário da plataforma (userId)")}
            value={draft.userId ?? ""}
            onChange={(v) => patch({ userId: v || undefined })}
          />
        </div>
      </Section>

      <Section title={t("contacts.sectionNotes", "Notas")}>
        <textarea
          value={draft.notes ?? ""}
          onChange={(e) => patch({ notes: e.target.value || undefined })}
          rows={4}
          className="w-full rounded border border-slate-300 bg-transparent px-2 py-1 text-sm dark:border-slate-700"
          data-testid="contact_notes"
        />
      </Section>

      {/* ── tier-change confirm (restates the tier's meaning — CT-INV-7/8) ── */}
      {tierConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          data-testid="tier_confirm_dialog"
        >
          <div className="max-w-md rounded-lg bg-white p-4 shadow-xl dark:bg-slate-900">
            <h4 className="mb-2 text-sm font-semibold">
              {t("contacts.tierConfirmTitle", "Confirmar mudança de nível de confiança")}
            </h4>
            <p className="mb-1 text-sm">
              <span className="font-medium">{TIER_META[tierConfirm].label}:</span>{" "}
              {TIER_META[tierConfirm].description}
            </p>
            {tierConfirm === "owner" && (
              <p className="mb-2 rounded bg-red-500/10 p-2 text-xs text-red-600">
                {t(
                  "contacts.ownerTierWarning",
                  "Nível TITULAR: este contato passa a ter o MESMO acesso que você por mensagem — casos, ferramentas, tudo. Só confirme se estas identidades são suas.",
                )}
              </p>
            )}
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setTierConfirm(null)}
                className="rounded border border-slate-300 px-3 py-1 text-sm dark:border-slate-700"
                data-testid="tier_confirm_cancel"
              >
                {t("common.cancel", "Cancelar")}
              </button>
              <button
                type="button"
                onClick={() => {
                  patch({ trustTier: tierConfirm });
                  setTierConfirm(null);
                }}
                className="rounded bg-amber-600 px-3 py-1 text-sm font-medium text-white"
                data-testid="tier_confirm_apply"
              >
                {t("common.confirm", "Confirmar")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
